import styled from "styled-components";
import { mobile } from "../../ui/media";

export const Section = styled.section`
  padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(3)}`};

  ${mobile} {
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(2)}`};
  }
`;

export const Panel = styled.div`
  position: relative;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: center;
  padding: ${({ theme }) => theme.spacing(6)};
  border-radius: ${({ theme }) => `${theme.radius.xl}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  overflow: hidden;

  /* Flat tinted discs, not gradients — solid fills at low opacity keep the surface honest. */
  &::before {
    content: "";
    position: absolute;
    top: -80px;
    right: -80px;
    width: 300px;
    height: 300px;
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.accent.primary};
    opacity: 0.08;
    pointer-events: none;
  }

  ${mobile} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing(3)};
    padding: ${({ theme }) => theme.spacing(3)};

    &::before {
      display: none;
    }
  }
`;

export const Copy = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const Kicker = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: ${({ theme }) => theme.spacing(0.75)};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.state.selected};
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.headingFamily};
  font-size: ${({ theme }) => theme.typography.h2};
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Body = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
  max-width: 440px;
`;

export const Perks = styled.ul`
  margin: ${({ theme }) => theme.spacing(1)} 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
  list-style: none;
`;

export const Perk = styled.li`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;

  svg {
    color: ${({ theme }) => theme.colors.accent.primary};
    flex-shrink: 0;
  }
`;

export const Form = styled.form`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.25)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => `${theme.radius.xl}px`};
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  box-shadow: ${({ theme }) => theme.colors.shadow.medium};

  ${mobile} {
    padding: ${({ theme }) => theme.spacing(2)};
  }
`;

export const Field = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0 ${({ theme }) => theme.spacing(2)};
  height: ${({ theme }) => theme.spacing(7)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  transition: border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.accent.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.state.selected};
  }

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus {
    outline: none;
  }
`;

export const Note = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;
